import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';

import axiosInstance from '../utils/axiosConfig';
import useTranslation from '../utils/useTranslation';
import Header from './Header';
import Footer from './Footer';
import {
  MediaThumb,
  isYouTubeItem,
  youTubeEmbedUrl,
} from './sections/GallerySection';

export default function AlbumPage() {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const { albumId } = useParams();

  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [lightItem, setLightItem] = useState(-1);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [albumId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get('public/gallery/', { params: { lang } })
      .then((res) => {
        if (cancelled) return;
        const found = (res.data?.albums || []).find((a) => String(a.id) === String(albumId));
        setAlbum(found || null);
      })
      .catch(() => {
        if (!cancelled) setAlbum(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [albumId, lang]);

  const allItems = album?.items || [];
  const photoCount = allItems.filter((it) => it.type === 'photo').length;
  const videoCount = allItems.filter((it) => it.type === 'video').length;
  const viewerItems = filter === 'all' ? allItems : allItems.filter((it) => it.type === filter);
  const lightMedia = lightItem >= 0 ? viewerItems[lightItem] : null;

  const closeLight = useCallback(() => setLightItem(-1), []);
  const nextLight = useCallback(
    () => setLightItem((i) => (i < viewerItems.length - 1 ? i + 1 : i)),
    [viewerItems.length],
  );
  const prevLight = useCallback(() => setLightItem((i) => (i > 0 ? i - 1 : i)), []);

  useEffect(() => {
    setLightItem(-1);
  }, [filter]);

  useEffect(() => {
    if (lightItem < 0) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLight();
      else if (e.key === 'ArrowRight') nextLight();
      else if (e.key === 'ArrowLeft') prevLight();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [lightItem, closeLight, nextLight, prevLight]);

  const tabs = [
    { value: 'all', label: t('gallery_filter_all'), count: allItems.length },
    { value: 'photo', label: t('gallery_filter_photos'), count: photoCount },
    { value: 'video', label: t('gallery_filter_videos'), count: videoCount },
  ].filter((tab) => tab.value === 'all' || tab.count > 0);

  const handleNavigate = (sectionId) => {
    if (sectionId === 'home') {
      navigate('/');
      return;
    }
    if (sectionId === 'about' || sectionId === 'laws') return;
    navigate('/', { state: { scrollTo: sectionId } });
  };

  return (
    <div className="gallery-page">
      <Header active="gallery" onNavigate={handleNavigate} />

      <main className="container album-page-wrap">
        {loading ? (
          <div className="album-page-loading">
            <div className="spinner" />
          </div>
        ) : !album ? (
          <div className="article-page-empty">
            <p>{t('gallery_filter_empty')}</p>
            <button type="button" className="btn btn-primary" onClick={() => navigate('/gallery')}>
              {t('back')}
            </button>
          </div>
        ) : (
          <>
            <header className="album-hero-head">
              <div className="album-hero-inner">
                <h1 className="album-page-title">{album.title}</h1>
              </div>
            </header>

            <div className="album-page-body">
              {tabs.length > 1 && (
                <div className="album-page-filters" role="tablist" aria-label={t('gallery_filter_label')}>
                  {tabs.map((tab) => (
                    <button
                      key={tab.value}
                      type="button"
                      role="tab"
                      aria-selected={filter === tab.value}
                      className={`gal-filter${filter === tab.value ? ' is-active' : ''}`}
                      onClick={() => setFilter(tab.value)}
                    >
                      <span className="gal-filter-label">{tab.label}</span>
                      <span className="gal-filter-count">{tab.count}</span>
                    </button>
                  ))}
                </div>
              )}

              {viewerItems.length === 0 ? (
                <p className="muted-note">{t('gallery_filter_empty')}</p>
              ) : (
                <ul className="gal-grid album-page-grid">
                  {viewerItems.map((item, idx) => {
                    const isVideo = item.type === 'video';
                    return (
                      <li key={item.id} className="gal-item">
                        <button
                          type="button"
                          className="gal-thumb"
                          onClick={() => setLightItem(idx)}
                          aria-label={isVideo ? t('gallery_type_video') : t('gallery_type_photo')}
                        >
                          <MediaThumb item={item} />
                          {isVideo && (
                            <span className="gal-play" aria-hidden="true">
                              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                                <polygon points="6 4 20 12 6 20 6 4" />
                              </svg>
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}
      </main>

      <Footer />

      {lightMedia &&
        createPortal(
          <div className="gal-light" role="dialog" aria-modal="true" onClick={closeLight}>
            <div className="gal-light-inner" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="gal-light-close" onClick={closeLight} aria-label={t('close')}>
                {'\u00D7'}
              </button>

              {album && (
                <div className="gal-light-caption">
                  <span className="gal-light-album">{album.title}</span>
                  {viewerItems.length > 1 && (
                    <span className="gal-light-counter">
                      {lightItem + 1} / {viewerItems.length}
                    </span>
                  )}
                </div>
              )}

              <div className="gal-light-media">
                {lightMedia.type === 'photo' ? (
                  <img src={lightMedia.src_url} alt="" />
                ) : isYouTubeItem(lightMedia) ? (
                  <iframe
                    src={`${youTubeEmbedUrl(lightMedia.src_url)}?autoplay=1&rel=0`}
                    title="YouTube video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  // eslint-disable-next-line jsx-a11y/media-has-caption
                  <video src={lightMedia.src_url} controls autoPlay playsInline />
                )}
              </div>

              {lightItem > 0 && (
                <button
                  type="button"
                  className="gal-light-nav gal-light-prev"
                  onClick={prevLight}
                  aria-label={t('gallery_prev')}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                </button>
              )}
              {lightItem < viewerItems.length - 1 && (
                <button
                  type="button"
                  className="gal-light-nav gal-light-next"
                  onClick={nextLight}
                  aria-label={t('gallery_next')}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
